import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';

import {
    deviceService,
    languageService,
    WSConnection,
    WSL_PAYMENT_TIMEOUT_MS,
} from '@unpispas/upp-base';

import { dataService } from '@unpispas/upp-data';
import { Pinpad } from '@unpispas/upp-data';
import { PinpadPay } from '@unpispas/upp-data';
import { Ticket } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';

/********************************/
/* Public types                 */
/********************************/

/** UI stage mapped from Pinpad progress literals. */
export type PinpadStage =
    | 'CONNECTING'
    | 'WAITING_CARD'
    | 'PIN'
    | 'PIN_RETRY'
    | 'CURRENCY'
    | 'SIGNATURE'
    | 'CUSTOMER'
    | 'PROCESSING'
    | 'CANCELLING'
    | 'APPROVED';

/** Final outcome of {@link PinpadCharge.Result} / {@link paytefService.Resolve}. */
export type PinpadOutcome =
    | 'APPROVED'
    | 'DENIED'
    | 'CANCELLED'
    | 'PENDING'
    | 'MISMATCH'
    | 'NOSERVICE'
    | 'FAILED'
    | 'IN_PROGRESS'
    | 'UNKNOWN';

/**
 * Why a charge did not end approved. Fixed by {@link paytefService}; the UI only maps it to text.
 * `UNKNOWN` means the print service reported a failure without a recognised cause.
 */
export type PinpadCause =
    | 'NO_SERVICE'
    | 'UNREACHABLE'
    | 'TERMINAL_OFFLINE'
    | 'PINPAD_DOWN'
    | 'UNAUTHORIZED'
    | 'BUSY'
    | 'START_FAILED'
    | 'LOST_TRACKING'
    | 'TIMEOUT'
    | 'NO_RESULT'
    | 'TERMINAL_CANCELLED'
    | 'DENIED'
    | 'AMOUNT_MISMATCH'
    | 'UNKNOWN';

/** Decision of {@link paytefService.CheckPrevious} about the previous attempt of a ticket. */
export type PinpadCheck = 'CLEAR' | 'APPROVED' | 'MISMATCH' | 'UNCERTAIN' | 'IN_PROGRESS';

/**
 * Why a terminal print did not succeed. Fixed by {@link paytefService}; the UI only maps it to text.
 * Distinct from {@link PinpadCause} (charge failures). `UNKNOWN` means the service answered without a
 * recognised print result or cause.
 */
export type PinpadPrintCause =
    | 'NO_SERVICE'
    | 'UNREACHABLE'
    | 'TERMINAL_OFFLINE'
    | 'BUSY'
    | 'NO_PRINTER'
    | 'PRINT_FAILED'
    | 'UNKNOWN';

/** Outcome of {@link paytefService.Print}: only `printed: true` when the service reports print success. */
export type PinpadPrintResult = {
    printed: boolean;
    cause: PinpadPrintCause | null;
};

/**
 * Printer presence and paper width from {@link paytefService.QueryPrinter}.
 * `null` means the terminal did not report the value (never invent one).
 */
export type PinpadPrinterInfo = {
    present: boolean | null;
    widthMm: number | null;
};

export type PinpadProgress = {
    stage: PinpadStage;
    raw?: string | null;
};

/**
 * Final result of a charge. `cause` is `null` only for `APPROVED` and for a cancellation
 * requested by this application; `canRetry` is `false` when a new charge makes no sense
 * (approved, or the terminal charged a different amount).
 */
export type PinpadResult = {
    outcome: PinpadOutcome;
    cause: PinpadCause | null;
    canRetry: boolean;
    approvedAfterCancel?: boolean;
    row?: PinpadPay | null;
};

/**
 * Outcome of {@link PinpadCharge.Manual}: `APPROVED` when the terminal had already approved the
 * charge (no manual declaration is made), `MANUAL` when the waiter's declaration was recorded.
 * `cancelConfirmed` tells whether the terminal confirmed the cancellation of the sale.
 */
export type PinpadManualResult = {
    outcome: 'APPROVED' | 'MANUAL';
    cancelConfirmed: boolean;
};

/** What a refund asks the terminal for: an amount to return, linked to a sale or loose. */
export type PinpadRefundRequest = {
    amountCents: number;
    /** Sale this refund belongs to; `null` for a loose refund (RF-09-09). */
    origin: PinpadPay | null;
};

/** Print-service command names (the wire names are provisional and live only here). */
/** WS `TARGET` registered by the local print-service as `paytef`. */
const PINPAD_MODULE = 'paytef';
const CMD_SALE = 'SALE';
const CMD_CANCEL = 'CANCEL';
/** Query of the last sale result of a terminal. */
const CMD_LASTRESULT = 'LASTRESULT';
/** Asks the terminal to return money, linked to a sale or loose. */
const CMD_REFUND = 'REFUND';
/** Send HTML (or image) to the terminal printer. */
const CMD_PRINT = 'PRINT';
/** Ask whether the terminal has a printer and its paper width. */
const CMD_QUERYPRINTER = 'QUERYPRINTER';

const CANCEL_WAIT_MS = 20000;

/** Print-only causes the print service may report (shared ones reuse `_ParseCause`). */
const PRINT_ONLY_CAUSES: ReadonlySet<PinpadPrintCause> = new Set<PinpadPrintCause>([
    'NO_PRINTER',
    'PRINT_FAILED',
]);

/********************************/
/* Charge session               */
/********************************/

/**
 * One in-flight card charge or refund: progress stream, final result, and cancel path.
 * Survives dialog destroy. Product close after APPROVED/MANUAL is {@link Settle}.
 */
export class PinpadCharge {
    private readonly _progress = new Subject<PinpadProgress>();
    readonly Progress$: Observable<PinpadProgress> = this._progress.asObservable();

    private _resultResolve: ((value: PinpadResult) => void) | null = null;
    private _resultSettled = false;
    readonly Result: Promise<PinpadResult>;

    private _saleConn: WSConnection | null = null;
    private _progressSub: { unsubscribe: () => void } | null = null;
    private _row: PinpadPay | null = null;
    private _cancelRequested = false;
    private _saleSent = false;
    private _cancelTask: Promise<void> | null = null;
    private _manualTask: Promise<PinpadManualResult> | null = null;
    private _held = false;
    private _cents = 0;
    private _reference = '';

    constructor(
        private readonly _owner: paytefService,
        private readonly _tickets: TicketView[],
        private readonly _terminal: Pinpad,
        private readonly _refund: PinpadRefundRequest | null = null,
    ) {
        this.Result = new Promise<PinpadResult>((resolve) => {
            this._resultResolve = resolve;
        });
        void this._Run();
    }

    /**
     * Opens a second connection and sends CANCEL; waits for {@link Result}. Calling it again (or
     * concurrently with {@link Manual}) joins the first request: only one CANCEL is sent.
     */
    Cancel(): Promise<void> {
        this._cancelTask ??= this._CancelOnce();
        return this._cancelTask;
    }

    /**
     * Gives up waiting for the terminal: cancels the sale, reads its final result and, unless that
     * result is an approval, records the waiter's manual-payment declaration (`MA`). An approval
     * is never declared manual. If the terminal does not answer the cancellation the declaration
     * is made anyway (`cancelConfirmed: false`) and a late sale result completes the `MA` row
     * without changing its state (see {@link paytefService.EvaluateResponse}).
     */
    Manual(): Promise<PinpadManualResult> {
        this._manualTask ??= this._ManualOnce();
        return this._manualTask;
    }

    /**
     * Product close after an approved or manually declared session (RN-PPV-04).
     * Sale: {@link paytefService.SettleSales} over the session tickets.
     * Refund: does **not** call {@link TicketView.CloseRefund} (host must `OnReturn` /
     * commit first — RN-TCP-14); returns whether `RefundToApply` is ready.
     * Non-closable outcomes (no row to apply) → `false`.
     */
    async Settle(): Promise<boolean> {
        if (this._refund) {
            return !!this._tickets[0]?.RefundToApply;
        }

        return this._owner.SettleSales(this._tickets);
    }

    private async _ManualOnce(): Promise<PinpadManualResult> {
        if (!this._resultSettled) {
            await this.Cancel();
        }

        const _result = await this.Result;
        if (_result.outcome === 'APPROVED') {
            return { outcome: 'APPROVED', cancelConfirmed: true };
        }

        await this._owner.DeclareManual(
            this._refund ? [this._tickets[0]] : this._tickets,
            this._terminal,
            this._refund ? 'RE' : 'SA',
        );
        return { outcome: 'MANUAL', cancelConfirmed: _result.outcome !== 'PENDING' };
    }

    private async _CancelOnce(): Promise<void> {
        this._cancelRequested = true;
        this._Emit({ stage: 'CANCELLING' });

        // Before the SALE is sent there is nothing to cancel on the terminal: `_Run` sees the
        // request and settles without sending it.
        let _cancelConn: WSConnection | null = null;
        try {
            _cancelConn = this._saleSent ? await this._owner.OpenTransport(this._terminal) : null;
            if (_cancelConn) {
                const _order = this._owner.BuildOrder(CMD_CANCEL, this._terminal, {
                    reference: this._reference,
                    amountCents: this._cents,
                });
                await _cancelConn.Send(PINPAD_MODULE, JSON.stringify(_order));
            }
        }
        catch (err) {
            this._owner.LogError(this._tickets[0] ?? null, this._terminal, err);
        }
        finally {
            _cancelConn?.Disconnect();
        }

        let _timer: ReturnType<typeof setTimeout> | undefined;
        const _timed = await Promise.race([
            this.Result.then((r) => ({ kind: 'result' as const, r })),
            new Promise<{ kind: 'timeout' }>((resolve) => {
                _timer = setTimeout(() => resolve({ kind: 'timeout' }), CANCEL_WAIT_MS);
            }),
        ]);
        clearTimeout(_timer);

        if (_timed.kind === 'timeout') {
            this._Settle(this._owner.BuildResult('PENDING', 'NO_RESULT', this._row));
        }
    }

    private async _Run(): Promise<void> {
        try {
            const _busy = this._owner.AcquireCharge(this._tickets, this._terminal);
            if (_busy) {
                this._Settle(_busy);
                return;
            }
            this._held = true;

            const _guard = this._refund
                ? this._owner.ValidateRefund(this._tickets, this._terminal, this._refund)
                : this._owner.ValidateCharge(this._tickets, this._terminal);
            if (_guard) {
                this._Settle(_guard);
                return;
            }

            this._Emit({ stage: 'CONNECTING' });

            this._saleConn = await this._owner.OpenTransport(this._terminal);
            if (!this._saleConn) {
                this._Settle(this._owner.BuildResult('NOSERVICE', 'NO_SERVICE', null));
                return;
            }

            this._cents = this._refund ? this._refund.amountCents : this._owner.AmountCents(this._tickets);
            this._reference = this._refund
                ? this._owner.RefundReference(this._tickets[0])
                : this._owner.Reference(this._tickets);

            this._row = await this._owner.RegisterAttempt(
                this._tickets,
                this._terminal,
                this._reference,
                this._cents,
                this._refund,
            );

            if (this._cancelRequested) {
                const _aborted = await this._owner.AbortAttempt(this._row);
                this._owner.SyncPayCopies(this._tickets, this._row);
                this._Settle(_aborted);
                return;
            }
            this._saleSent = true;

            this._progressSub = this._saleConn.OnProgress.subscribe((frame) => {
                this._Emit(this._owner.MapProgress(frame));
            });

            const _order = this._owner.BuildOrder(this._refund ? CMD_REFUND : CMD_SALE, this._terminal, {
                reference: this._reference,
                amountCents: this._cents,
                operation: this._refund?.origin?.operation ?? undefined,
            });

            const _response = await this._saleConn.Send(
                PINPAD_MODULE,
                JSON.stringify(_order),
                { timeoutMs: WSL_PAYMENT_TIMEOUT_MS },
            );

            const _evaluated = await this._owner.EvaluateResponse(
                _response,
                this._row,
                this._cents,
                this._cancelRequested,
            );
            // EvaluateResponse mutates the canonical row; mirror onto open edit copies
            // so PinpadToApply / PinpadPending match before SettleSales / ApplyPinpad.
            this._owner.SyncPayCopies(this._tickets, this._row);
            this._Settle(_evaluated);
        }
        catch (err) {
            this._owner.LogError(this._tickets[0] ?? null, this._terminal, err);
            this._Settle(this._owner.BuildResult('PENDING', 'LOST_TRACKING', this._row));
        }
        finally {
            if (this._held) {
                this._owner.ReleaseCharge(this._tickets, this._terminal);
                this._held = false;
            }
            this._progressSub?.unsubscribe();
            this._progressSub = null;
            this._saleConn?.Disconnect();
            this._saleConn = null;
            this._progress.complete();
        }
    }

    private _Emit(progress: PinpadProgress): void {
        if (!this._progress.closed) {
            this._progress.next(progress);
        }
    }

    private _Settle(result: PinpadResult): void {
        if (this._resultSettled) {
            return;
        }
        this._resultSettled = true;
        this._resultResolve?.(result);
        this._resultResolve = null;
    }
}

/********************************/
/* Service                      */
/********************************/

@Injectable({ providedIn: 'root' })
export class paytefService {
    /**
     * Known Pinpad `transactionStatus` / `cardStatus` → stage.
     * Unknown literals fall through to PROCESSING (catalog is data-only).
     */
    private static readonly _STATUS_CATALOG: ReadonlyArray<{
        match: RegExp;
        stage: PinpadStage;
    }> = [
        { match: /^waitingForCard$/i, stage: 'WAITING_CARD' },
        { match: /^waitingForUser$/i, stage: 'CUSTOMER' },
        { match: /^processing$/i, stage: 'PROCESSING' },
        { match: /^finished$/i, stage: 'APPROVED' },
        { match: /^cancelled$/i, stage: 'CANCELLING' },
        { match: /^error$/i, stage: 'PROCESSING' },
        { match: /^timeout$/i, stage: 'PROCESSING' },
        { match: /pin.?retry/i, stage: 'PIN_RETRY' },
        { match: /\bpin\b/i, stage: 'PIN' },
        { match: /currency|moneda/i, stage: 'CURRENCY' },
        { match: /signature|firma/i, stage: 'SIGNATURE' },
        { match: /application|confirm|contact|mobile|alternate/i, stage: 'CUSTOMER' },
    ];

    /**
     * When true, `PRINT` asks the service to rasterise the receipt before printing (QR legibility).
     * Flip only after a real-paper check proves HTML QR is unreadable on the terminal.
     */
    private static readonly PRINT_AS_IMAGE = false;

    /** Causes the print service may report; the rest are fixed on this side. */
    private static readonly _SERVICE_CAUSES: ReadonlySet<PinpadCause> = new Set<PinpadCause>([
        'UNREACHABLE',
        'TERMINAL_OFFLINE',
        'PINPAD_DOWN',
        'UNAUTHORIZED',
        'BUSY',
        'START_FAILED',
        'TIMEOUT',
        'NO_RESULT',
        'TERMINAL_CANCELLED',
        'DENIED',
    ]);

    /** Causes after which the terminal is known not to have charged (row `DE`). */
    private static readonly _DISCARDED_CAUSES: ReadonlySet<PinpadCause> = new Set<PinpadCause>([
        'UNREACHABLE',
        'TERMINAL_OFFLINE',
        'PINPAD_DOWN',
        'UNAUTHORIZED',
        'BUSY',
        'TERMINAL_CANCELLED',
        'DENIED',
    ]);

    /** `_uuid` of the tickets and terminals with a charge in progress in this application. */
    private readonly _busyTickets = new Set<string>();
    private readonly _busyTerminals = new Set<string>();

    constructor(
        private readonly devices: deviceService,
        private readonly lang: languageService,
        private readonly data: dataService,
    ) {}

    /**
     * Registers a charge in progress for the tickets and the terminal. Only one charge per ticket
     * and per terminal may run from this application: a second one is refused with `BUSY`
     * without sending anything or creating an attempt row (it is never queued). Queries
     * (`Resolve`, `CheckPrevious`) do not occupy the terminal.
     * Returns the refusal, or `null` when the charge was registered (release it with
     * {@link ReleaseCharge}).
     */
    AcquireCharge(tickets: TicketView[], terminal: Pinpad): PinpadResult | null {
        const _ticketKeys = tickets.map((t) => t.object._uuid);
        if (this._busyTerminals.has(terminal._uuid) || _ticketKeys.some((k) => this._busyTickets.has(k))) {
            this.LogError(tickets[0] ?? null, terminal, 'charge refused: another charge in progress');
            return this.BuildResult('FAILED', 'BUSY', null);
        }

        this._busyTerminals.add(terminal._uuid);
        _ticketKeys.forEach((k) => this._busyTickets.add(k));
        return null;
    }

    /** Releases the registration made by {@link AcquireCharge}. */
    ReleaseCharge(tickets: TicketView[], terminal: Pinpad): void {
        this._busyTerminals.delete(terminal._uuid);
        tickets.forEach((t) => this._busyTickets.delete(t.object._uuid));
    }

    /**
     * Settles one or more approved/manual card attempts after a terminal session.
     * Preflights {@link TicketView.ReadyPinpadApply} on every ticket (all-or-nothing:
     * an `AP` mismatch marks that row `MM` and pays none); then {@link TicketView.ApplyPinpad}
     * on each. Single-ticket hosts may call `ApplyPinpad` directly.
     */
    async SettleSales(tickets: TicketView[]): Promise<boolean> {
        if (!tickets?.length || !tickets.some((t) => !!t.PinpadToApply)) {
            return false;
        }

        for (const _ticket of tickets) {
            if (!(await _ticket.ReadyPinpadApply())) {
                return false;
            }
        }

        for (const _ticket of tickets) {
            if (!(await _ticket.ApplyPinpad())) {
                return false;
            }
        }

        return true;
    }

    /** Starts a charge session for one or more tickets paid with a single terminal operation. */
    Charge(tickets: TicketView[], terminal: Pinpad): PinpadCharge {
        return new PinpadCharge(this, tickets ?? [], terminal);
    }

    /** Starts a refund session for a ticket, linked to a sale (`request.origin`) or loose. */
    Refund(ticket: TicketView, terminal: Pinpad, request: PinpadRefundRequest): PinpadCharge {
        return new PinpadCharge(this, [ticket], terminal, request);
    }

    /**
     * Queries the terminal for the last operation matching a pending (`PE`) attempt of the given
     * kind across the ticket set. One LASTRESULT for the attempt reference; the outcome is
     * propagated to sibling rows. Does not consult `MM` rows (outcome already known).
     */
    async Resolve(
        tickets: TicketView[],
        terminal: Pinpad,
        kind: 'SA' | 'RE' = 'SA',
    ): Promise<PinpadOutcome> {
        if (!tickets?.length) {
            return 'UNKNOWN';
        }

        let _pending: PinpadPay | null = null;
        for (const _ticket of tickets) {
            const _row = this._PendingRow(_ticket, kind);
            if (_row?.status === 'PE') {
                _pending = _row;
                break;
            }
        }
        if (!_pending) {
            return 'UNKNOWN';
        }

        let _conn: WSConnection | null = null;
        try {
            _conn = await this.OpenTransport(terminal);
            if (!_conn) {
                return 'UNKNOWN';
            }

            const _siblings = this._SiblingRows(_pending);
            const _cents = _siblings.reduce(
                (sum, row) => sum + Math.round(Number(row.amount ?? 0) * 100),
                0,
            );
            const _order = this.BuildOrder(CMD_LASTRESULT, terminal, {
                reference: String(_pending.reference ?? ''),
                amountCents: _cents,
            });

            const _response = await _conn.Send(PINPAD_MODULE, JSON.stringify(_order));
            const _outcome = await this._ApplyResolveResponse(_response, _pending, _cents, kind);
            this._MirrorPayCopies(tickets, _siblings);
            return _outcome;
        }
        catch (err) {
            this.LogError(tickets[0] ?? null, terminal, err);
            return 'UNKNOWN';
        }
        finally {
            _conn?.Disconnect();
        }
    }

    /**
     * Decides, before retrying or declaring a manual attempt, whether the previous attempt of the
     * ticket set (sale or refund, per `kind`) is already resolved. Only a pending (`PE`) attempt is
     * queried on the terminal (once for the shared reference): `APPROVED` (still to close with
     * `ApplyPinpad` / `SettleSales`), `CLEAR`, `MISMATCH`, `IN_PROGRESS` or `UNCERTAIN`. Callers
     * that receive `IN_PROGRESS`/`UNCERTAIN` must poll {@link Resolve} (status-first) instead of
     * starting a new charge. For a refund (`kind === 'RE'`) an unregistered approval does not
     * short-circuit to `APPROVED` (DD-09-07). Mixed states across the set yield `UNCERTAIN`. It
     * never closes the ticket.
     */
    async CheckPrevious(
        tickets: TicketView[],
        terminal: Pinpad,
        kind: 'SA' | 'RE' = 'SA',
    ): Promise<PinpadCheck> {
        if (!tickets?.length) {
            return 'CLEAR';
        }

        if (kind === 'SA') {
            const _withApply = tickets.filter((t) => !!t.PinpadToApply);
            if (_withApply.length === tickets.length) {
                return 'APPROVED';
            }
            if (_withApply.length > 0) {
                return 'UNCERTAIN';
            }
        }

        const _pendings = tickets.map((t) => this._PendingRow(t, kind));
        if (_pendings.every((row) => !row)) {
            return 'CLEAR';
        }

        if (_pendings.some((row) => row?.status === 'MM')) {
            return 'MISMATCH';
        }

        if (!_pendings.some((row) => row?.status === 'PE')) {
            return 'UNCERTAIN';
        }

        const _outcome = await this.Resolve(tickets, terminal, kind);
        if (_outcome === 'APPROVED') {
            return 'APPROVED';
        }
        if (_outcome === 'DENIED') {
            return 'CLEAR';
        }
        if (_outcome === 'IN_PROGRESS') {
            return 'IN_PROGRESS';
        }

        return 'UNCERTAIN';
    }

    /**
     * Records the waiter's declaration that the customer paid (or was refunded) outside the
     * terminal flow. For a sale, every ticket in the set gets an `MA` row **in memory** (pending
     * siblings of the same attempt become `MA` together; otherwise a new multi-row attempt is
     * created). Sale `MA` is **not** flushed here: persistence is the host's `ApplyPinpad` +
     * `OnCommit` / `CommitTicket` (same atomic flush as `applied`, so the server never sees
     * `MA` without the paid ticket). For a refund, only `tickets[0]` is declared and that row
     * is committed (refunds stay single-ticket). Never queries the terminal and never closes
     * the ticket. Idempotent while unapplied `MA` rows already cover the set.
     */
    async DeclareManual(
        tickets: TicketView[],
        terminal: Pinpad | null,
        kind: 'SA' | 'RE' = 'SA',
    ): Promise<void> {
        if (!tickets?.length) {
            return;
        }

        if (kind === 'RE') {
            if (!terminal) {
                return;
            }
            await this._DeclareManualRefund(tickets[0], terminal);
            return;
        }

        // Idempotent only when every ticket already has unapplied MA and no PE/MM left
        // (an older MA must not skip converting a newer pending attempt).
        if (
            tickets.every((t) => t.PinpadToApply?.status === 'MA')
            && tickets.every((t) => !t.PinpadPending)
        ) {
            return;
        }

        let _seed: PinpadPay | null = null;
        for (const _ticket of tickets) {
            // Prefer a live PE/MM on the object so a stale PinpadPays view cache cannot
            // spawn a second attempt while one is still pending. Always resolve to the
            // canonical PinpadPay (edit copies must not be committed with a null objid).
            _seed =
                this._PendingRow(_ticket, 'SA') ||
                this._CanonicalPay(
                    _ticket.object.pinpadpays.find(
                        (row) => !row.IsRefund && (row.status === 'PE' || row.status === 'MM'),
                    ) || null,
                );
            if (_seed) {
                break;
            }
        }

        if (!_seed) {
            if (!terminal) {
                return;
            }
            // New MA rows attach to persisted tickets, not volatile edit copies.
            _seed = this._NewAttempt(
                this._CanonicalTicketViews(tickets),
                terminal,
                this.Reference(tickets),
                this.AmountCents(tickets),
            );
        }

        const _siblings = this._SiblingRows(_seed);
        for (const _row of _siblings) {
            _row.status = 'MA';
            _row.finished = new Date();
        }
        // Sale MA stays local until ApplyPinpad + host ticket commit (RN-TCP-05 / RN-TCP-08).
        this._MirrorPayCopies(tickets, _siblings);
        this._RefreshPinpadViews(tickets, _siblings);
    }

    /** Invalidates `PinpadPays` caches so `PinpadToApply` sees in-memory `MA` before flush. */
    private _RefreshPinpadViews(tickets: TicketView[], rows: PinpadPay[]): void {
        const _seen = new Set<Ticket>();
        for (const _ticket of tickets) {
            const _obj = _ticket?.object;
            if (_obj && !_seen.has(_obj)) {
                _seen.add(_obj);
                _obj.DoRefresh('PINPADPAY', false);
            }
        }
        for (const _row of rows) {
            const _obj = _row.ticket;
            if (_obj && !_seen.has(_obj)) {
                _seen.add(_obj);
                _obj.DoRefresh('PINPADPAY', false);
            }
        }
    }

    private async _DeclareManualRefund(
        ticket: TicketView,
        terminal: Pinpad,
    ): Promise<void> {
        if (ticket.RefundToApply?.status === 'MA') {
            return;
        }

        let _row = this._PendingRow(ticket, 'RE');
        if (!_row) {
            _row = this._NewAttempt(
                this._CanonicalTicketViews([ticket]),
                terminal,
                this.RefundReference(ticket),
                0,
                { amountCents: 0, origin: null },
            );
        }

        _row.status = 'MA';
        _row.finished = new Date();
        await _row.DoCommit(true);
        this._MirrorPayCopies([ticket], [_row]);
    }

    /**
     * Discards every pending (`PE`) attempt of the ticket set of the given kind as `DE`. Sibling
     * rows of a joint charge are settled together. Refused (logged, no change) while a charge is
     * in flight for any ticket of the set. `MM`, `AP` and `MA` rows are untouched.
     */
    async Supersede(tickets: TicketView[], kind: 'SA' | 'RE' = 'SA'): Promise<void> {
        if (!tickets?.length) {
            return;
        }

        for (const _ticket of tickets) {
            if (this._busyTickets.has(_ticket.object._uuid)) {
                this.LogError(
                    _ticket,
                    null,
                    new Error('supersede refused: a charge is in flight for the ticket'),
                );
                return;
            }
        }

        const _seen = new Set<PinpadPay>();
        for (const _ticket of tickets) {
            const _rows = kind === 'RE' ? _ticket.RefundPays : _ticket.PinpadPays;
            for (const _row of _rows.filter((row) => row.status === 'PE')) {
                const _canonical = this._CanonicalPay(_row);
                if (!_canonical || _seen.has(_canonical)) {
                    continue;
                }
                const _siblings = this._SiblingRows(_canonical);
                for (const _sib of _siblings) {
                    _seen.add(_sib);
                }
                console.warn(
                    `[PAYTEF] ticket=${_ticket.object?.objid ?? _ticket.object?._uuid ?? '?'} superseded=${_siblings.length} pending attempt(s)`,
                );
                await this._SettleRow(_canonical, 'DENIED', true);
                this._MirrorPayCopies(tickets, _siblings);
            }
        }
    }

    /****************************/
    /* Shared helpers (Charge)  */
    /****************************/

    ValidateCharge(
        tickets: TicketView[],
        terminal: Pinpad | null,
    ): PinpadResult | null {
        if (!terminal || !terminal.rasppi?.id) {
            return this.BuildResult('NOSERVICE', 'NO_SERVICE', null);
        }

        if (!tickets?.length || this.AmountCents(tickets) <= 0) {
            return this.BuildResult('FAILED', 'UNKNOWN', null);
        }

        for (const _ticket of tickets) {
            if (_ticket.PinpadPending) {
                this.LogError(_ticket, terminal, 'charge refused: previous attempt unresolved');
                return this.BuildResult('FAILED', 'UNKNOWN', null);
            }
        }

        return null;
    }

    /**
     * Guards a refund request the same way {@link ValidateCharge} guards a sale, plus an
     * unresolved previous refund (RN-P09-04). Does not look at `PinpadPending`: a pending sale does
     * not prevent a refund (they cannot coexist in practice — an unpaid ticket has nothing to
     * refund).
     */
    ValidateRefund(
        tickets: TicketView[],
        terminal: Pinpad | null,
        request: PinpadRefundRequest,
    ): PinpadResult | null {
        if (!terminal || !terminal.rasppi?.id) {
            return this.BuildResult('NOSERVICE', 'NO_SERVICE', null);
        }

        if (!tickets?.length || request.amountCents <= 0) {
            return this.BuildResult('FAILED', 'UNKNOWN', null);
        }

        for (const _ticket of tickets) {
            if (_ticket.RefundPending) {
                this.LogError(_ticket, terminal, 'refund refused: previous attempt unresolved');
                return this.BuildResult('FAILED', 'UNKNOWN', null);
            }
        }

        return null;
    }

    /**
     * Single place that builds a {@link PinpadResult}: `canRetry` is false for an approved
     * charge and for an amount mismatch, true otherwise.
     */
    BuildResult(
        outcome: PinpadOutcome,
        cause: PinpadCause | null,
        row: PinpadPay | null,
        approvedAfterCancel = false,
    ): PinpadResult {
        const _result: PinpadResult = {
            outcome,
            cause,
            canRetry: outcome !== 'APPROVED' && cause !== 'AMOUNT_MISMATCH',
            row,
        };
        if (approvedAfterCancel) {
            _result.approvedAfterCancel = true;
        }
        return _result;
    }

    async OpenTransport(terminal: Pinpad): Promise<WSConnection | null> {
        const _name = terminal.rasppi?.id;
        if (!_name) {
            return null;
        }

        // Local print-service WS (not PINPAD.port / pinpad HTTP).
        return this.devices.Connect(
            '127.0.0.1',
            5005,
            String(_name),
            true,
        );
    }

    /**
     * Reprints a stored card voucher (`merchant` / `customer`) on the terminal.
     * Payloads from Paytef are plain text (epic 04 samples); they are escaped and wrapped in
     * `<pre>` for `contentType: html`. Missing copy → `PRINT_FAILED` without sending.
     */
    async PrintVoucher(
        terminal: Pinpad,
        row: PinpadPay,
        copy: 'merchant' | 'customer',
    ): Promise<PinpadPrintResult> {
        const _raw = row.Receipts[copy];
        if (!_raw) {
            console.error('[PAYTEF] voucher copy missing: ' + copy);
            return { printed: false, cause: 'PRINT_FAILED' };
        }

        return this.Print(terminal, paytefService._VoucherHtml(_raw));
    }

    /**
     * Escapes plain-text voucher lines and wraps them for the HTML print channel.
     */
    private static _VoucherHtml(text: string): string {
        const _escaped = text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
        return '<pre style="white-space:pre-wrap;margin:0;font-family:monospace">' +
            _escaped +
            '</pre>';
    }

    /**
     * Sends an HTML document to the terminal printer through the local print service.
     * Returns `printed: true` only when the service reports print success — never because the
     * transport alone succeeded. Connection is always released.
     */
    async Print(terminal: Pinpad, html: string): Promise<PinpadPrintResult> {
        if (!html) {
            console.error('[PAYTEF] print refused: empty content');
            return { printed: false, cause: 'PRINT_FAILED' };
        }

        let _conn: WSConnection | null = null;
        try {
            _conn = await this.OpenTransport(terminal);
            if (!_conn) {
                this.LogError(null, terminal, 'print refused: no print service');
                return { printed: false, cause: 'NO_SERVICE' };
            }

            const _order = this.BuildOrder(CMD_PRINT, terminal, { content: html });
            const _response = await _conn.Send(PINPAD_MODULE, JSON.stringify(_order));
            const _result = paytefService._ParsePrintResponse(_response);
            if (!_result.printed) {
                this.LogError(null, terminal, `print failed: ${_result.cause}`);
            }
            return _result;
        }
        catch (err) {
            this.LogError(null, terminal, err);
            return { printed: false, cause: 'UNKNOWN' };
        }
        finally {
            _conn?.Disconnect();
        }
    }

    /**
     * Asks whether the terminal has a printer and its paper width in millimetres.
     * Never invents values: missing or unsupported answers yield `null` fields. Does not throw.
     */
    async QueryPrinter(terminal: Pinpad): Promise<PinpadPrinterInfo> {
        let _conn: WSConnection | null = null;
        try {
            _conn = await this.OpenTransport(terminal);
            if (!_conn) {
                return { present: null, widthMm: null };
            }

            const _order = this.BuildOrder(CMD_QUERYPRINTER, terminal, {});
            const _response = await _conn.Send(PINPAD_MODULE, JSON.stringify(_order));
            return paytefService._ParsePrinterInfo(_response);
        }
        catch (err) {
            this.LogError(null, terminal, err);
            return { present: null, widthMm: null };
        }
        finally {
            _conn?.Disconnect();
        }
    }

    async RegisterAttempt(
        tickets: TicketView[],
        terminal: Pinpad,
        reference: string,
        amountCents: number,
        refund: PinpadRefundRequest | null = null,
    ): Promise<PinpadPay> {
        // Keep panel edit copies intact (no Overwrite): PE attaches to the persisted
        // ticket, then a mirror row is linked onto each open copy so PinpadPending
        // is visible without rebuilding the editor.
        for (let _i = 0; _i < tickets.length; _i++) {
            // Payment request leaves draft: never persist/show a ticket still in `PR`.
            if (tickets[_i].status === 'PR') {
                tickets[_i].status = 'AC';
            }

            const _object = tickets[_i].object;
            const _canonical = (_object?.CopyOf as Ticket) || _object;
            if (_canonical && _canonical !== _object && _canonical.status === 'PR') {
                _canonical.status = 'AC';
            }

            // New cart tickets (no objid, not an edit copy) must insert before PINPADPAY FK.
            if (_object && !_object.objid && !_object.CopyOf) {
                await _object.DoCommit(true);
            }
        }

        const _canonicalViews = this._CanonicalTicketViews(tickets);
        const _primary = this._NewAttempt(
            _canonicalViews,
            terminal,
            reference,
            amountCents,
            refund,
        );
        const _siblings = this._SiblingRows(_primary);
        for (const _row of _siblings) {
            _row.status = 'PE';
            await _row.DoCommit(true);
        }
        this._AttachAttemptCopies(tickets, _siblings);
        this._RefreshPinpadViews(tickets, _siblings);
        return _primary;
    }

    /**
     * Settles a charge that was cancelled before its sale was sent: the row registered for it (if
     * any) becomes `DE` and the result is `CANCELLED`.
     */
    async AbortAttempt(row: PinpadPay | null): Promise<PinpadResult> {
        return row ? this._SettleRow(row, null, true) : this.BuildResult('CANCELLED', null, null);
    }

    /**
     * Creates the attempt row(s) for a sale or refund.
     * A multi-ticket sale yields one {@link PinpadPay} per ticket (same reference,
     * same full `orders` JSON, same `started`); each `amount` is that ticket's due.
     * Returns the primary row (`tickets[0]`) for the charge session.
     */
    private _NewAttempt(
        tickets: TicketView[],
        terminal: Pinpad,
        reference: string,
        amountCents: number,
        refund: PinpadRefundRequest | null = null,
    ): PinpadPay {
        const _orders = JSON.stringify(
            ticketsOrdernos(tickets).map((n) => (n == null ? '' : String(n))),
        );
        // Sibling matching keys on reference+started ms; bump if a prior attempt on the
        // same tickets already used this key (same-millisecond cancel→manual must not pair).
        const _started = this._UniqueAttemptStarted(tickets, reference);

        if (refund) {
            const _row = new PinpadPay(null, this.data);
            _row.reference = reference;
            _row.orders = _orders;
            _row.amount = amountCents / 100;
            _row.pinpad = terminal;
            _row.started = _started;
            _row.ticket = tickets[0].object;
            _row.kind = 'RE';
            _row.origin = refund.origin?.operation ?? null;
            return _row;
        }

        const _rows: PinpadPay[] = [];
        for (const _ticket of tickets) {
            const _row = new PinpadPay(null, this.data);
            _row.reference = reference;
            _row.orders = _orders;
            const _due = Number(_ticket.due ?? _ticket.object?.price ?? 0);
            _row.amount = Math.round(_due * 100) / 100;
            _row.pinpad = terminal;
            _row.started = _started;
            _row.ticket = _ticket.object;
            _rows.push(_row);
        }

        this._RememberAttemptFamily(_rows);
        return _rows[0];
    }

    /** `started` that does not collide with an existing pinpadpay of the same reference. */
    private _UniqueAttemptStarted(tickets: TicketView[], reference: string): Date {
        let _ms = Date.now();
        const _taken = new Set<number>();
        for (const _ticket of tickets) {
            for (const _pay of _ticket.object?.pinpadpays ?? []) {
                if (String(_pay.reference ?? '') !== String(reference) || !_pay.started) {
                    continue;
                }
                _taken.add(new Date(_pay.started).getTime());
            }
        }
        while (_taken.has(_ms)) {
            _ms += 1;
        }
        return new Date(_ms);
    }

    /** In-memory family of multi-ticket attempt rows (same reference + started). */
    private _attemptFamilies = new Map<string, PinpadPay[]>();

    private _AttemptFamilyKey(row: PinpadPay): string | null {
        if (!row.reference || !row.started) {
            return null;
        }
        return `${row.reference}|${new Date(row.started).getTime()}`;
    }

    private _RememberAttemptFamily(rows: PinpadPay[]): void {
        if (rows.length < 2) {
            return;
        }
        const _key = this._AttemptFamilyKey(rows[0]);
        if (_key) {
            this._attemptFamilies.set(_key, rows);
        }
    }

    /**
     * Rows of one multi-ticket attempt: same `reference` and `started`.
     * Each row keeps its own per-ticket `amount`.
     */
    private _SiblingRows(row: PinpadPay): PinpadPay[] {
        const _key = this._AttemptFamilyKey(row);
        if (_key) {
            const _cached = this._attemptFamilies.get(_key);
            if (_cached?.length) {
                return _cached;
            }
        }

        const _ref = row.reference;
        const _startedMs = row.started ? new Date(row.started).getTime() : null;
        if (!_ref || _startedMs == null) {
            return [row];
        }

        const _found: PinpadPay[] = [];
        const _seen = new Set<PinpadPay>();
        const _add = (candidate: PinpadPay | null | undefined) => {
            if (!candidate || _seen.has(candidate)) {
                return;
            }
            if (String(candidate.reference ?? '') !== String(_ref)) {
                return;
            }
            if (!candidate.started || new Date(candidate.started).getTime() !== _startedMs) {
                return;
            }
            _seen.add(candidate);
            _found.push(candidate);
        };

        _add(row);

        const _ticket = row.ticket;
        const _pool = _ticket?.qrcode?.tickets?.length
            ? _ticket.qrcode.tickets
            : (_ticket?.place?.tickets?.length ? _ticket.place.tickets : (_ticket ? [_ticket] : []));

        for (const _t of _pool) {
            for (const _pay of _t.pinpadpays) {
                _add(_pay);
            }
        }

        return _found.length ? _found : [row];
    }

    /** Copies terminal outcome fields to siblings; does not overwrite per-ticket `amount`. */
    private _CopyAttemptOutcome(source: PinpadPay, target: PinpadPay): void {
        target.status = source.status;
        target.operation = source.operation;
        target.authcode = source.authcode;
        target.terminalcode = source.terminalcode;
        target.finished = source.finished;
        target.signature = source.signature;
        target.receipts = source.receipts;
    }

    private async _CommitAttemptFamily(row: PinpadPay): Promise<void> {
        for (const _sibling of this._SiblingRows(row)) {
            if (_sibling !== row) {
                this._CopyAttemptOutcome(row, _sibling);
            }
            await _sibling.DoCommit(true);
        }
    }

    /** TicketViews for the persisted tickets behind any edit copies (no Overwrite). */
    private _CanonicalTicketViews(tickets: TicketView[]): TicketView[] {
        return tickets.map((ticket) => {
            const _raw = ticket?.object;
            const _canonical = (_raw?.CopyOf as Ticket) || _raw;
            if (!_canonical || _canonical === _raw) {
                return ticket;
            }
            return this.data.alives.get(_canonical) as TicketView;
        });
    }

    /**
     * Links a PinpadPay mirror onto each open edit-copy ticket for the new canonical
     * attempt rows. CopyOf PinpadPays skip DoChildLinked AddPinpadpay, so the child
     * list is updated explicitly. No-op when the panel ticket is already canonical.
     */
    private _AttachAttemptCopies(panelTickets: TicketView[], canonicalRows: PinpadPay[]): void {
        for (const _panel of panelTickets) {
            const _panelObj = _panel?.object;
            const _panelCanonical = _panelObj?.CopyOf as Ticket | null;
            if (!_panelObj || !_panelCanonical) {
                continue;
            }

            for (const _canonical of canonicalRows) {
                const _rowTicket = ((_canonical.ticket?.CopyOf as Ticket) || _canonical.ticket) ?? null;
                if (_rowTicket !== _panelCanonical) {
                    continue;
                }

                const _already = (_panelObj.pinpadpays ?? []).some(
                    (pay) => pay === _canonical || this._CanonicalPay(pay) === _canonical,
                );
                if (_already) {
                    continue;
                }

                const _mirror = _canonical.Copy([]) as PinpadPay;
                _mirror.ticket = _panelObj;
                _panelObj.AddPinpadpay(_mirror);
            }
        }
    }

    /** Follows CopyOf to the persisted PinpadPay (edit copies have null objid). */
    private _CanonicalPay(row: PinpadPay | null | undefined): PinpadPay | null {
        if (!row) {
            return null;
        }

        let _row = row;
        let _up = _row.CopyOf;
        while (_up instanceof PinpadPay) {
            _row = _up;
            _up = _row.CopyOf;
        }
        return _row;
    }

    /**
     * Mirrors a canonical attempt's outcome onto open edit-copy `PinpadPay` rows
     * (same as {@link Resolve} / {@link DeclareManual} after mutating the family).
     * Call after {@link EvaluateResponse} / {@link AbortAttempt} on a charge session.
     */
    SyncPayCopies(tickets: TicketView[], row: PinpadPay | null | undefined): void {
        if (!row || !tickets?.length) {
            return;
        }
        this._MirrorPayCopies(tickets, this._SiblingRows(row));
    }

    /**
     * Copies outcome fields from canonical rows onto edit-copy PinpadPays still
     * held by the panel, then refreshes PINPADPAY view caches.
     */
    private _MirrorPayCopies(tickets: TicketView[], rows: PinpadPay[]): void {
        const _canonicals = new Set<PinpadPay>();
        for (const _row of rows) {
            const _canonical = this._CanonicalPay(_row);
            if (_canonical) {
                _canonicals.add(_canonical);
            }
        }
        if (!_canonicals.size) {
            return;
        }

        for (const _ticket of tickets) {
            let _touched = false;
            for (const _pay of _ticket.object?.pinpadpays ?? []) {
                const _canonical = this._CanonicalPay(_pay);
                if (!_canonical || !_canonicals.has(_canonical) || _pay === _canonical) {
                    continue;
                }
                this._CopyAttemptOutcome(_canonical, _pay);
                _pay.applied = _canonical.applied;
                _touched = true;
            }
            if (_touched) {
                _ticket.object.DoRefresh('PINPADPAY', false);
            }
        }
    }

    /** Picks the pending/mismatch row of a ticket for the given attempt kind. */
    private _PendingRow(ticket: TicketView, kind: 'SA' | 'RE'): PinpadPay | null {
        const _row = kind === 'RE' ? ticket?.RefundPending ?? null : ticket?.PinpadPending ?? null;
        return this._CanonicalPay(_row);
    }

    Reference(tickets: TicketView[]): string {
        if (!tickets?.length) {
            return '';
        }

        const _first = sanitizeReference(String(tickets[0].object?.orderno ?? ''));
        if (tickets.length === 1) {
            return _first.slice(0, 32);
        }

        const _suffix = `+${tickets.length - 1}`;
        const _budget = Math.max(0, 32 - _suffix.length);
        return `${_first.slice(0, _budget)}${_suffix}`;
    }

    /**
     * Reference for a refund attempt: `'R' + {@link Reference}`, truncated to 32 characters.
     * From the second live refund of the ticket onward (`RefundPays.length > 0`) appends a
     * `-<n>` suffix, still within 32 characters (DD-09-02).
     */
    RefundReference(ticket: TicketView): string {
        const _base = ('R' + this.Reference([ticket])).slice(0, 32);
        const _count = ticket.RefundPays.length;
        if (_count === 0) {
            return _base;
        }

        const _suffix = `-${_count + 1}`;
        const _budget = Math.max(0, 32 - _suffix.length);
        return `${_base.slice(0, _budget)}${_suffix}`;
    }

    AmountCents(tickets: TicketView[]): number {
        let _sum = 0;
        for (const _ticket of tickets) {
            const _amount = Number(_ticket.due ?? _ticket.object?.price ?? 0);
            _sum += Math.round(_amount * 100);
        }
        return _sum;
    }

    PinpadLanguage(): string {
        const _raw = (this.lang.GetLanguage(false) || 'es').toLowerCase();
        if (_raw === 'es' || _raw === 'en' || _raw === 'eu') {
            return _raw;
        }
        return 'es';
    }

    BuildOrder(
        command: string,
        terminal: Pinpad,
        opts: { reference?: string; amountCents?: number; content?: string; operation?: string },
    ): Record<string, unknown> {
        const _order: Record<string, unknown> = {
            command,
            host: terminal.host || '127.0.0.1',
            port: Number(terminal.port) || 8887,
            pinpad: terminal.device?.name || '*',
        };

        if (command === CMD_PRINT) {
            _order['content'] = opts.content ?? '';
            _order['generateImage'] = paytefService.PRINT_AS_IMAGE;
        }
        else if (command === CMD_QUERYPRINTER) {
            // pinpad / host / port only
        }
        else {
            _order['reference'] = opts.reference ?? '';
            _order['language'] = this.PinpadLanguage();
            _order['createReceipt'] = true;
            if (command === CMD_SALE || command === CMD_LASTRESULT || command === CMD_REFUND) {
                _order['amount'] = opts.amountCents ?? 0;
            }
            if (command === CMD_REFUND && opts.operation) {
                _order['operation'] = opts.operation;
            }
        }

        if (terminal.apikey) {
            _order['apikey'] = terminal.apikey;
        }

        return _order;
    }

    MapProgress(frame: Record<string, unknown> | null | undefined): PinpadProgress {
        const _raw =
            (typeof frame?.['transactionStatus'] === 'string'
                ? frame['transactionStatus']
                : null) ||
            (typeof frame?.['cardStatus'] === 'string' ? frame['cardStatus'] : null) ||
            (typeof frame?.['status'] === 'string' ? frame['status'] : null) ||
            '';

        if (!_raw || _raw === 'started' || _raw === 'progress') {
            if (_raw === 'started') {
                return { stage: 'CONNECTING', raw: _raw };
            }
            return { stage: 'PROCESSING', raw: _raw || null };
        }

        for (const _entry of paytefService._STATUS_CATALOG) {
            if (_entry.match.test(_raw)) {
                return { stage: _entry.stage, raw: _raw };
            }
        }

        return { stage: 'PROCESSING', raw: _raw };
    }

    /**
     * Turns the answer to a `SALE` or `REFUND` order into the final {@link PinpadResult} and
     * leaves the attempt row in the state that matches it. This is the only place that approves a
     * charge or a refund (`row.IsRefund` tells which one the row is).
     * Failure causes come only from the service payload (`cause`); free text is never parsed.
     * A `TIMEOUT` failure may carry the terminal's final reading in `result`.
     */
    async EvaluateResponse(
        response: { exec?: string; data?: unknown } | null,
        row: PinpadPay,
        sentCents: number,
        cancelRequested: boolean,
    ): Promise<PinpadResult> {
        if (row.status === 'MA') {
            return await this._CompleteManual(response, row, sentCents);
        }

        if (!response || response.exec === 'TIMEOUT') {
            return this.BuildResult('PENDING', 'LOST_TRACKING', row);
        }

        const _payload = parseJsonObject(response.data);
        let _sale = _payload;

        if (response.exec !== 'SUCCESS' || _payload?.['status'] === 'error') {
            const _cause = paytefService._ParseCause(_payload?.['cause'], 'UNKNOWN');
            const _read = _cause === 'TIMEOUT' ? parseJsonObject(_payload?.['result']) : null;
            if (_read?.['approved'] !== true) {
                return await this._SettleRow(row, _cause, _read != null);
            }
            _sale = _read;
        }

        if (!_sale) {
            return await this._SettleRow(row, 'UNKNOWN', false);
        }

        if (_sale['failure'] === true || _sale['failed'] === true || _sale['approved'] !== true) {
            const _cause = paytefService._ParseCause(_sale['cause'], 'DENIED');
            const _byApp = cancelRequested && (_cause === 'DENIED' || _cause === 'TERMINAL_CANCELLED');
            return await this._SettleRow(row, _byApp ? null : _cause, true);
        }

        const _paidCents = paidCentsFromPayload(_sale, sentCents);
        if (_paidCents !== sentCents) {
            // Single-ticket: store the terminal amount on the row. Multi-ticket: keep
            // each sibling's per-ticket due so SettleSales / ApplyPinpad can still compare correctly.
            if (this._SiblingRows(row).length === 1) {
                row.amount = _paidCents / 100;
            }
            this._FillApprovedFields(row, _sale);
            return await this._SettleRow(row, 'AMOUNT_MISMATCH', true);
        }

        row.status = 'AP';
        this._FillApprovedFields(row, _sale);
        await this._CommitAttemptFamily(row);

        return this.BuildResult('APPROVED', null, row, cancelRequested);
    }

    /**
     * Late sale result for a row the waiter already declared manual (`MA`): the declaration is
     * never rewritten. An approval for the expected amount only completes the terminal data of the
     * row (so a refund can find it). The returned result is ignored: the charge is already settled.
     */
    private async _CompleteManual(
        response: { exec?: string; data?: unknown } | null,
        row: PinpadPay,
        sentCents: number,
    ): Promise<PinpadResult> {
        const _payload = parseJsonObject(response?.data);
        const _read =
            response?.exec === 'SUCCESS' && _payload?.['status'] !== 'error'
                ? _payload
                : parseJsonObject(_payload?.['result']);

        const _approved =
            _read != null &&
            _read['approved'] === true &&
            _read['failure'] !== true &&
            _read['failed'] !== true &&
            paidCentsFromPayload(_read, sentCents) === sentCents;

        if (!_approved) {
            return this.BuildResult('FAILED', 'UNKNOWN', row);
        }

        this._FillApprovedFields(row, _read);
        await this._CommitAttemptFamily(row);
        return this.BuildResult('APPROVED', null, row, true);
    }

    LogError(
        ticket: TicketView | null,
        terminal: Pinpad | null,
        err: unknown,
    ): void {
        const _ticketId = ticket?.object?.objid ?? ticket?.object?._uuid ?? '?';
        const _term =
            terminal?.PinpadName || terminal?.objid || '?';
        const _cause = err instanceof Error ? err.message : String(err);
        console.error(
            `[PAYTEF] ticket=${_ticketId} terminal=${_term} cause=${_cause}`,
        );
    }

    private async _ApplyResolveResponse(
        response: { exec?: string; data?: unknown } | null,
        row: PinpadPay,
        expectedCents: number,
        kind: 'SA' | 'RE' = 'SA',
    ): Promise<PinpadOutcome> {
        if (!response || response.exec !== 'SUCCESS' || response.data == null) {
            return 'UNKNOWN';
        }

        const _payload = parseJsonObject(response.data);
        if (!_payload) {
            return 'UNKNOWN';
        }

        if (_payload['busy'] === true || _payload['occupied'] === true) {
            return 'IN_PROGRESS';
        }

        if (
            _payload['match'] === false ||
            _payload['noMatch'] === true ||
            _payload['free'] === true
        ) {
            await this._SettleRow(row, 'DENIED', true);
            return 'DENIED';
        }

        const _opType = String(_payload['opType'] ?? _payload['op_type'] ?? '').toLowerCase();
        const _expectedOpType = kind === 'RE' ? 'refund' : 'sale';
        if (_opType && _opType !== _expectedOpType) {
            return 'UNKNOWN';
        }

        const _ref = String(
            _payload['transactionReference'] ?? _payload['reference'] ?? '',
        );
        if (_ref && _ref !== String(row.reference ?? '')) {
            return 'UNKNOWN';
        }

        const _paid = paidCentsFromPayload(_payload, expectedCents);
        if (_paid !== expectedCents) {
            return 'UNKNOWN';
        }

        const _when = finishedDateFromPayload(_payload);
        if (_when && row.started && _when.getTime() < new Date(row.started).getTime()) {
            return 'UNKNOWN';
        }

        if (_payload['approved'] !== true) {
            if (_payload['approved'] === false) {
                await this._SettleRow(row, 'DENIED', true);
                return 'DENIED';
            }
            return 'UNKNOWN';
        }

        row.status = 'AP';
        this._FillApprovedFields(row, _payload);
        await this._CommitAttemptFamily(row);
        return 'APPROVED';
    }

    /**
     * Maps a failure cause to the state of the attempt row and commits it when it changes:
     * `DE` when the terminal is known not to have charged (or the app cancelled, `cause` null),
     * `MM` for a different amount, `PE` when the result is unknown. `finalRead` tells whether the
     * terminal's final result was read after a `TIMEOUT`. Sibling rows of a multi-ticket attempt
     * receive the same outcome (each keeps its own amount).
     */
    private async _SettleRow(
        row: PinpadPay,
        cause: PinpadCause | null,
        finalRead: boolean,
    ): Promise<PinpadResult> {
        let _status: 'DE' | 'MM' | 'PE' = 'PE';
        if (cause === null || paytefService._DISCARDED_CAUSES.has(cause) || (cause === 'TIMEOUT' && finalRead)) {
            _status = 'DE';
        }
        else if (cause === 'AMOUNT_MISMATCH') {
            _status = 'MM';
        }

        if (row.status !== _status) {
            row.status = _status;
            if (_status === 'DE') {
                row.finished = new Date();
            }
            await this._CommitAttemptFamily(row);
        }

        let _outcome: PinpadOutcome = 'FAILED';
        if (cause === null) {
            _outcome = 'CANCELLED';
        }
        else if (_status === 'PE') {
            _outcome = 'PENDING';
        }
        else if (_status === 'MM') {
            _outcome = 'MISMATCH';
        }
        else if (cause === 'DENIED') {
            _outcome = 'DENIED';
        }

        return this.BuildResult(_outcome, cause, row);
    }

    private static _ParseCause(raw: unknown, fallback: PinpadCause): PinpadCause {
        const _cause = typeof raw === 'string' ? (raw.toUpperCase() as PinpadCause) : null;
        return _cause && paytefService._SERVICE_CAUSES.has(_cause) ? _cause : fallback;
    }

    /**
     * Maps a print-service answer to {@link PinpadPrintResult}. `printed` is true only when the
     * payload reports print success (`printed: true`). Shared failure causes reuse {@link _ParseCause};
     * free text is never turned into a typed cause.
     */
    private static _ParsePrintResponse(
        response: { exec?: string; data?: unknown } | null,
    ): PinpadPrintResult {
        if (!response || response.exec !== 'SUCCESS') {
            const _payload = parseJsonObject(response?.data);
            if (_payload?.['status'] === 'error') {
                return { printed: false, cause: paytefService._ParsePrintCause(_payload['cause']) };
            }
            return { printed: false, cause: 'UNKNOWN' };
        }

        const _payload = parseJsonObject(response.data);
        if (!_payload) {
            return { printed: false, cause: 'UNKNOWN' };
        }

        if (_payload['status'] === 'error') {
            return { printed: false, cause: paytefService._ParsePrintCause(_payload['cause']) };
        }

        if (_payload['printed'] === true) {
            return { printed: true, cause: null };
        }

        if (_payload['printed'] === false) {
            return { printed: false, cause: paytefService._ParsePrintCause(_payload['cause']) };
        }

        // Transport OK but no print-result field (current print service) → not printed.
        return { printed: false, cause: 'UNKNOWN' };
    }

    /**
     * Resolves a print failure cause: shared literals go through {@link _ParseCause}; print-only
     * literals (`NO_PRINTER`, `PRINT_FAILED`) are accepted here; anything else is `UNKNOWN`.
     */
    private static _ParsePrintCause(raw: unknown): PinpadPrintCause {
        if (typeof raw === 'string') {
            const _upper = raw.toUpperCase() as PinpadPrintCause;
            if (PRINT_ONLY_CAUSES.has(_upper)) {
                return _upper;
            }
        }

        const _shared = paytefService._ParseCause(raw, 'UNKNOWN');
        if (
            _shared === 'UNREACHABLE' ||
            _shared === 'TERMINAL_OFFLINE' ||
            _shared === 'BUSY'
        ) {
            return _shared;
        }

        return 'UNKNOWN';
    }

    private static _ParsePrinterInfo(
        response: { exec?: string; data?: unknown } | null,
    ): PinpadPrinterInfo {
        if (!response || response.exec !== 'SUCCESS') {
            return { present: null, widthMm: null };
        }

        const _payload = parseJsonObject(response.data);
        if (!_payload || _payload['status'] === 'error') {
            return { present: null, widthMm: null };
        }

        const _presentRaw = _payload['present'] ?? _payload['hasPrinter'];
        let _present: boolean | null = null;
        if (typeof _presentRaw === 'boolean') {
            _present = _presentRaw;
        }

        const _widthRaw = _payload['widthMm'] ?? _payload['width'];
        let _widthMm: number | null = null;
        if (typeof _widthRaw === 'number' && Number.isFinite(_widthRaw) && _widthRaw > 0) {
            _widthMm = _widthRaw;
        }

        if (_present === null && _widthMm === null) {
            return { present: null, widthMm: null };
        }

        if (_present === false) {
            return { present: false, widthMm: null };
        }

        return { present: _present, widthMm: _widthMm };
    }

    private _FillApprovedFields(
        row: PinpadPay,
        payload: Record<string, unknown>,
    ): void {
        const _op = payload['paytefOperationNumber'] ?? payload['operation'];
        if (_op != null) {
            row.operation = String(_op);
        }

        const _auth = payload['authorisationCode'] ?? payload['authcode'];
        if (_auth != null) {
            row.authcode = String(_auth);
        }

        const _termCode =
            payload['terminalCode'] ??
            payload['terminalcode'] ??
            nestedString(payload['cardInformation'], 'terminalId');
        if (typeof _termCode === 'string' && _termCode) {
            row.terminalcode = _termCode;
        }

        row.finished = finishedDateFromPayload(payload) || new Date();

        const _sig = payload['signature'] ?? payload['signatureRequired'];
        if (typeof _sig === 'boolean') {
            row.signature = _sig;
        }

        const _receipts = payload['receipts'];
        if (_receipts != null) {
            row.receipts =
                typeof _receipts === 'string' ? _receipts : JSON.stringify(_receipts);
        }
    }
}

/********************************/
/* Pure helpers                 */
/********************************/

function sanitizeReference(raw: string): string {
    return String(raw)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^A-Za-z0-9-]/g, '');
}

function ticketsOrdernos(tickets: TicketView[]): Array<string | number | null> {
    return tickets.map((t) => t.object?.orderno ?? null);
}

function parseJsonObject(data: unknown): Record<string, unknown> | null {
    if (data == null) {
        return null;
    }
    if (typeof data === 'object' && !Array.isArray(data)) {
        return data as Record<string, unknown>;
    }
    if (typeof data === 'string') {
        try {
            const _parsed = JSON.parse(data);
            if (_parsed && typeof _parsed === 'object' && !Array.isArray(_parsed)) {
                return _parsed as Record<string, unknown>;
            }
        }
        catch {
            return null;
        }
    }
    return null;
}

function paidCentsFromPayload(
    payload: Record<string, unknown>,
    fallback: number,
): number {
    const _raw =
        payload['requestedAmount'] ??
        payload['amount'] ??
        payload['authorizedAmount'] ??
        fallback;
    const _num = Number(_raw);
    if (!Number.isFinite(_num)) {
        return fallback;
    }
    // Paytef amounts are already cents when coming from our SALE order echo.
    return Math.round(_num);
}

function finishedDateFromPayload(
    payload: Record<string, unknown>,
): Date | null {
    const _raw =
        payload['finished'] ??
        payload['completedAt'] ??
        payload['timestamp'] ??
        payload['endTime'];
    if (_raw == null) {
        return null;
    }
    if (_raw instanceof Date) {
        return _raw;
    }
    const _date = new Date(String(_raw));
    return Number.isNaN(_date.getTime()) ? null : _date;
}

function nestedString(
    parent: unknown,
    key: string,
): string | null {
    if (!parent || typeof parent !== 'object') {
        return null;
    }
    const _val = (parent as Record<string, unknown>)[key];
    return _val == null ? null : String(_val);
}
